// src/components/CreateGroupModal.tsx
import { logger } from '../utils/logger';
// ðŸ›¡ï¸ ENHANCED: Block protection - can't add blocked users to groups
// ðŸ”§ FIXED: Added autocapitalize="sentences" to inputs
// ðŸ†• ADDED: Group limit check (3 groups max for free users)

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonInput,
  IonTextarea,
  IonList,
  IonSpinner,
  IonText,
  IonAvatar,
  IonAlert,
  IonSearchbar,
} from '@ionic/react';
import { close, checkmark } from 'ionicons/icons';
import { getConnections, Connection } from '../services/connectionsService';
import { createGroup } from '../services/groupService';
import { canCreateGroup } from '../services/subscriptionService';
import { filterBlockedUsers } from '../services/blockProtectionService';
import { moderateContent } from '../services/contentModeration';
import { PremiumPaywall } from './PremiumPaywall';
import { AvatarPreview } from './avatar/AvatarPreview';
import { updateDataWithRetry } from '../services/retryService';
import { handleError, ErrorMessages } from '../services/errorHandler';
import './CreateGroupModal.css';

interface CreateGroupModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onGroupCreated?: (groupId: string) => void;
}

interface ConnectionDisplay {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  avatar?: any;
  is_admin?: boolean;
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onDismiss,
  onGroupCreated,
}) => {
  const [connections, setConnections] = useState<ConnectionDisplay[]>([]);
  const [filteredConnections, setFilteredConnections] = useState<ConnectionDisplay[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [imagesEnabled, setImagesEnabled] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      checkGroupLimit();
      loadContent();
      // Reset form
      setSelectedUserIds(new Set());
      setGroupName('');
      setDescription('');
      setImagesEnabled(false);
      setSearchText('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredConnections(connections);
    } else {
      const search = searchText.toLowerCase();
      setFilteredConnections(
        connections.filter(
          c =>
            c.username.toLowerCase().includes(search) ||
            c.display_name.toLowerCase().includes(search)
        )
      );
    }
  }, [searchText, connections]);

  const checkGroupLimit = async () => {
    const { allowed, reason } = await canCreateGroup();

    if (!allowed && reason) {
      setLimitMessage(reason);
      setShowLimitAlert(true);
    }
  };

  const loadContent = async () => {
    setLoading(true);

    try {
      const result = await getConnections();

      if (result.success && result.connections) {
        // Map connections to the format needed for selection
        const accepted = result.connections
          .filter((c: Connection) => c.status === 'accepted')
          .map((c: Connection) => ({
            id: c.other_user.id,
            username: c.other_user.username,
            display_name: c.other_user.display_name,
            avatar_url: c.other_user.avatar_url,
            avatar: c.other_user.avatar,
            is_admin: c.other_user.is_admin,
          }));

        // Filter out blocked users
        const userIds = accepted.map(c => c.id);
        const allowedIds = await filterBlockedUsers(userIds);
        const allowedSet = new Set(allowedIds);

        const availableConnections = accepted.filter(c => allowedSet.has(c.id));

        setConnections(availableConnections);
        setFilteredConnections(availableConnections);
      } else {
        logger.error('Failed to load connections:', result.error);
        setConnections([]);
        setFilteredConnections([]);
      }
    } catch (error) {
      logger.error('Error loading connections:', error);
      setConnections([]);
      setFilteredConnections([]);
    }

    setLoading(false);
  };

  const toggleUser = (userId: string) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };

  const handleCreate = async () => {
    if (!groupName.trim() || selectedUserIds.size < 1) {
      return;
    }

    // Content moderation for group name
    const nameCheck = moderateContent(groupName.trim());
    if (!nameCheck.isClean) {
      setErrorAlertMessage(nameCheck.reason || 'Group name contains inappropriate content');
      setShowErrorAlert(true);
      return;
    }

    // Content moderation for description (if provided)
    if (description.trim()) {
      const descCheck = moderateContent(description.trim());
      if (!descCheck.isClean) {
        setErrorAlertMessage(descCheck.reason || 'Group description contains inappropriate content');
        setShowErrorAlert(true);
        return;
      }
    }

    // Check group limit before creating
    const { allowed, reason } = await canCreateGroup();

    if (!allowed) {
      setLimitMessage(reason || 'Cannot create group');
      setShowPaywall(true);
      return;
    }

    setCreating(true);

    try {
      const result = await updateDataWithRetry(
        () => createGroup(
          groupName,
          Array.from(selectedUserIds),
          description || undefined,
          imagesEnabled
        )
      );

      if (result.success && result.data?.group) {
        if (onGroupCreated) {
          onGroupCreated(result.data.group.id);
        }
        onDismiss();
      } else {
        const errorMsg = result.error?.userMessage || 'Failed to create group. Please try again.';
        setErrorAlertMessage(errorMsg);
        setShowErrorAlert(true);
      }
    } catch (error) {
      const appError = handleError(error, 'create group');
      setErrorAlertMessage(appError.userMessage);
      setShowErrorAlert(true);
    }

    setCreating(false);
  };

  const handleLimitAlertDismiss = () => {
    setShowLimitAlert(false);
    onDismiss();
  };

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onDismiss} disabled={creating}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
            <IonTitle>Create Group</IonTitle>
            <IonButtons slot="end">
              <IonButton
                onClick={handleCreate}
                disabled={!groupName.trim() || selectedUserIds.size < 1 || creating}
                strong
              >
                {creating ? <IonSpinner name="crescent" /> : <IonIcon icon={checkmark} />}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="create-group-container">
          <div className="group-info-section">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Group Name *</IonLabel>
                <IonInput
                  value={groupName}
                  onIonInput={(e) => setGroupName(e.detail.value!)}
                  placeholder="Enter group name"
                  maxlength={50}
                  disabled={creating}
                  autocapitalize="sentences"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Description (Optional)</IonLabel>
                <IonTextarea
                  value={description}
                  onIonInput={(e) => setDescription(e.detail.value!)}
                  placeholder="What's this group about?"
                  maxlength={200}
                  rows={3}
                  disabled={creating}
                  autocapitalize="sentences"
                />
              </IonItem>

              <IonItem lines="none">
                <IonCheckbox
                  checked={imagesEnabled}
                  onIonChange={(e) => setImagesEnabled(e.detail.checked)}
                  disabled={creating}
                  slot="start"
                />
                <IonLabel>
                  <h3>Enable Image Sharing</h3>
                  <p style={{ fontSize: '14px', color: 'var(--ion-color-medium)' }}>
                    Allow members to share images in this group
                  </p>
                </IonLabel>
              </IonItem>
            </IonList>
          </div>

          <div className="members-section">
            <div className="section-header">
              <h3>Select Members</h3>
              <p>
                <IonText color="medium">
                  Choose at least 1 person to create a group
                  {selectedUserIds.size > 0 && ` Â· ${selectedUserIds.size} selected`}
                </IonText>
              </p>
            </div>

            <IonSearchbar
              value={searchText}
              onIonInput={(e) => setSearchText(e.detail.value!)}
              placeholder="Search connections..."
              disabled={creating}
              style={{ padding: '0 0 16px 0' }}
            />

            <div>
              {loading ? (
                <div className="loading-container">
                  <IonSpinner name="crescent" />
                </div>
              ) : filteredConnections.length === 0 ? (
                <div className="empty-state">
                  <IonText color="medium">
                    {connections.length === 0 ? (
                      <>
                        <p>You don't have any available connections.</p>
                        <p>Connect with people first to create a group!</p>
                      </>
                    ) : (
                      <p>No connections match your search.</p>
                    )}
                  </IonText>
                </div>
              ) : (
                <IonList>
                  {filteredConnections.map((connection) => (
                    <IonItem
                      key={connection.id}
                      button
                      onClick={() => !creating && toggleUser(connection.id)}
                      disabled={creating}
                    >
                      <IonAvatar slot="start">
                        {connection.avatar_url ? (
                          <img
                            src={connection.avatar_url}
                            alt={connection.display_name || connection.username}
                          />
                        ) : connection.avatar ? (
                            <AvatarPreview
                              avatarData={connection.avatar}
                              size={40}
                              userId={connection.id}
                              isAdmin={connection.is_admin || false}
                            />
                        ) : (
                          <AvatarPreview size={40} userId={connection.id} />
                        )}
                      </IonAvatar>
                      <IonLabel>
                        <h2>{connection.display_name || connection.username}</h2>
                        {connection.display_name && (
                          <p>@{connection.username}</p>
                        )}
                      </IonLabel>
                      <IonCheckbox
                        slot="end"
                        checked={selectedUserIds.has(connection.id)}
                        disabled={creating}
                      />
                    </IonItem>
                  ))}
                </IonList>
              )}
            </div>
          </div>
        </IonContent>
      </IonModal>

      <IonAlert
        isOpen={showLimitAlert}
        onDidDismiss={handleLimitAlertDismiss}
        header="Group Limit Reached"
        message={limitMessage}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Upgrade to Premium',
            handler: () => {
              setShowLimitAlert(false);
              setShowPaywall(true);
            },
          },
        ]}
      />

      <PremiumPaywall
        isOpen={showPaywall}
        onDismiss={() => {
          setShowPaywall(false);
          onDismiss();
        }}
        feature="groups"
      />

      <IonAlert
        isOpen={showErrorAlert}
        onDidDismiss={() => setShowErrorAlert(false)}
        header="Invalid Content"
        message={errorAlertMessage}
        buttons={['OK']}
      />
    </>
  );
};